import { useFormContext } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { patchRun } from '../../../api/runs';
import { EditOperationMode, isOperationLocked } from '../../SessionView/SessionDetails/SessionConfigs/editOperationUtils';

/**
 * Outcome of a move, so callers can word their own feedback:
 *  - 'saved'   the PATCH went out and the list was invalidated
 *  - 'queued'  staged into the session form; lands on the next save
 *  - 'locked'  another edit operation holds the lock; nothing happened
 *  - 'skipped' nothing to move (planned run, or config not in the list)
 */
export type RunConfigMoveResult = 'saved' | 'queued' | 'locked' | 'skipped';

/**
 * Report a move outcome, whichever one it was. Callers used to word only the
 * two they expected, so the deferred path's 'queued' and the not-in-form
 * 'skipped' closed the menu in silence — the first reading as "done" when the
 * write is still pending an explicit Save, the second as "done" when nothing
 * happened at all. `destination` names where the run was headed, e.g.
 * 'Discards'.
 */
export function toastMoveResult(result: RunConfigMoveResult, destination: string): void {
  switch (result) {
    case 'saved':
      toast.success(`Run moved to ${destination}`);
      break;
    case 'queued':
      toast.success(`Run will move to ${destination} on Save`);
      break;
    case 'locked':
      toast.error('Save pending changes first');
      break;
    default:
      toast.error(`Could not move run to ${destination}`);
  }
}

interface RunConfigMoveOptions {
  run: any;
  config: any;
  configs?: any[];
  sessions?: any[] | null;
  invalidateRuns?: any;
  configMovesToSave?: { [key: string]: () => Promise<void> };
  editOpMode?: EditOperationMode;
  setEditOpMode?: (mode: EditOperationMode) => void;
}

/**
 * Moves a run to another config using the commit strategy that fits the caller.
 *
 * Two callers, two strategies. The cross-session run list passes `sessions` and
 * owns no pending edit set, so the move is written straight away. SessionDetails
 * passes no `sessions` and holds the whole config/run tree in an unsaved form —
 * there an immediate PATCH plus invalidate refetches the session out from under
 * those edits and drops them, so the move is staged into the form and queued in
 * `configMovesToSave` to go out with everything else on save.
 *
 * Presence of `sessions` is the discriminator ConfigSelector has always used;
 * it is kept here so both callers agree rather than each deciding for itself.
 */
export function useRunConfigMove(opts: RunConfigMoveOptions) {
  const { run, config, configs, sessions, invalidateRuns, configMovesToSave, editOpMode, setEditOpMode } = opts;
  const { getValues, setValue } = useFormContext();

  const isDeferred = !sessions;

  /** Stage the move in the session form and queue the write for the next save. */
  const stageMove = (newConfigId: string): RunConfigMoveResult => {
    if (!configs) return 'skipped';

    const fromConfigIndex = configs.findIndex((c: any) => c.id === config?.id);
    const toConfigIndex = configs.findIndex((c: any) => c.id === newConfigId);
    // A -1 index would write to the field path `configs.-1.runs`, silently
    // corrupting the form instead of failing, so bail on either miss.
    if (fromConfigIndex === -1 || toConfigIndex === -1) return 'skipped';

    const fromConfigRuns = getValues(`configs.${fromConfigIndex}.runs`) || [];
    const toConfigRuns = getValues(`configs.${toConfigIndex}.runs`) || [];

    const fromRunIndex = fromConfigRuns.findIndex((r: any) => r.id === run.id);
    if (fromRunIndex !== -1) {
      const updatedFromRuns = [...fromConfigRuns];
      updatedFromRuns.splice(fromRunIndex, 1);
      setValue(`configs.${fromConfigIndex}.runs`, updatedFromRuns);
    }

    // Keep the destination ordered by run_number rather than appending blindly.
    const toRunIndex = toConfigRuns.findIndex((existingRun: any) => existingRun.run_number > run.run_number);
    const updatedToRuns = [...toConfigRuns];
    if (toRunIndex === -1) updatedToRuns.push(run);
    else updatedToRuns.splice(toRunIndex, 0, run);
    setValue(`configs.${toConfigIndex}.runs`, updatedToRuns);

    // Key by run.id, not by target config, so several runs heading for the same
    // config don't overwrite one another's pending write.
    if (configMovesToSave) {
      configMovesToSave[run.id] = () => patchRun(run.id, { config_id: newConfigId });
    }
    return 'queued';
  };

  const moveRunToConfig = async (newConfigId: string): Promise<RunConfigMoveResult> => {
    if (!run?.id || !newConfigId) return 'skipped';

    if (!isDeferred) {
      await patchRun(run.id, { config_id: newConfigId });
      invalidateRuns?.();
      return 'saved';
    }

    if (isOperationLocked(editOpMode, 'run_move')) return 'locked';
    setEditOpMode?.('run_move');
    return stageMove(newConfigId);
  };

  return { moveRunToConfig, isDeferred };
}
